import React, { useEffect, useState } from 'react'
import { MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Select } from '@runeffective/evo-design-system'
import { Button, Flex, HighlightedText } from '@ui'
import { IUIButtonProps } from '@ui/buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import { get, values } from 'lodash'
import api, { ISpaceType } from '@api'
import ClearableSelectWrapper from './ClearableSelectWrapper'
import space from '@api/space'

interface SpaceTypeSelectProps {
  btnText?: React.ReactNode
  value: ISpaceType | null
  onChange: (spaceType: ISpaceType) => void
  onSelectAllChange?: (spaceTypes: ISpaceType[]) => void
  buttonProps?: IUIButtonProps
  itemDisabled?: (spaceType: ISpaceType) => boolean
  multi?: boolean
  filterable?: boolean
  values?: ISpaceType[]
  shouldIncludeSelectAll?: boolean
  seatableSpaceTypesOnly?: boolean
}

const BPSelect = Select2<ISpaceType>

export default function SpaceTypeSelect({
  btnText,
  value,
  onChange,
  onSelectAllChange,
  buttonProps,
  itemDisabled,
  multi,
  values,
  filterable = true,
  shouldIncludeSelectAll,
  seatableSpaceTypesOnly,
}: SpaceTypeSelectProps) {
  const allSpaceTypes: ISpaceType[] = useSpaceTypes()
  const spaceTypes = seatableSpaceTypesOnly
    ? allSpaceTypes.filter((x) => x.interactivity === 'seatable')
    : allSpaceTypes
  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const [selectedOption, setSelectedOption] = useState({ id: '', displayName: '' })
  enum AllOption {
    SELECT_ALL_ID = 'select-all',
    SELECT_ALL_NAME = 'Select All',
  }
  const selectAllOption = { id: AllOption.SELECT_ALL_ID, displayName: AllOption.SELECT_ALL_NAME }
  const spaceTypesWithSelectAll = [selectAllOption, ...spaceTypes] as ISpaceType[]

  const handleSelectAllChange = () => {
    if (selectAllChecked) {
      onSelectAllChange?.(spaceTypes)
    } else {
      onSelectAllChange?.([])
    }
    setSelectedOption({ id: '', displayName: '' })
  }

  useEffect(() => {
    if (spaceTypes.length === values?.length) {
      setSelectAllChecked(true)
    } else {
      setSelectAllChecked(false)
    }
    selectedOption.id === AllOption.SELECT_ALL_ID && handleSelectAllChange()
  }, [values?.length, spaceTypes.length, selectAllChecked])
  return (
    <ClearableSelectWrapper>
      <BPSelect
        filterable={filterable}
        resetOnSelect
        popoverProps={{
          minimal: true,
          position: Position.BOTTOM,
        }}
        noResults={<MenuItem disabled text="No results" />}
        items={shouldIncludeSelectAll ? spaceTypesWithSelectAll : spaceTypes}
        itemRenderer={(spaceType, { handleClick, modifiers, query }) =>
          modifiers.matchesPredicate ? (
            <MenuItem
              shouldDismissPopover={multi ? false : true}
              active={modifiers.active}
              key={spaceType.name}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                setSelectedOption(spaceType)
                if (spaceType.id === AllOption.SELECT_ALL_ID) {
                  e.preventDefault()
                  setSelectAllChecked(!selectAllChecked)
                  // handleSelectAllChange()
                } else {
                  if (multi) {
                    e.preventDefault()
                    onChange(spaceType)
                    return
                  }
                  handleClick(e)
                }
              }}
              text={
                <Flex align="center">
                  {/* {multi && <Checkbox checked={itemDisabled && itemDisabled(spaceType)} />} */}
                  {multi && (
                    <Checkbox
                      checked={
                        spaceType.id === AllOption.SELECT_ALL_ID
                          ? selectAllChecked
                          : itemDisabled && itemDisabled(spaceType)
                      }
                    />
                  )}
                  <HighlightedText text={spaceType.displayName} query={query} />
                </Flex>
              }
            />
          ) : null
        }
        itemListPredicate={filterSpaceTypes}
        // onItemSelect={onChange}
        onItemSelect={(item) => {
          if (item.id === AllOption.SELECT_ALL_ID) {
            handleSelectAllChange()
          } else {
            onChange(item)
          }
        }}>
        <Button
          {...buttonProps}
          text={value ? value.displayName : btnText || 'Select'}
          rightIconName={!get(buttonProps, 'iconName') && !value ? 'chevron-down' : undefined}
        />
      </BPSelect>
      <div />
    </ClearableSelectWrapper>
  )
}

const filterSpaceTypes: ItemListPredicate<ISpaceType> = (query, spaceTypes) => {
  return query ? fuzzy.filter(spaceTypes, query, { key: 'displayName' }) : spaceTypes
}

let persistedState: ISpaceType[] = []

export function useSpaceTypes(): ISpaceType[] {
  const [spaceTypes, setSpaceTypes] = React.useState<ISpaceType[]>(persistedState)

  useEffect(() => {
    api.company.spaceTypes().then((spaceTypes) => setSpaceTypes(spaceTypes))
  }, [])

  useEffect(() => {
    // Cache initial list
    if (!persistedState.length && spaceTypes.length) {
      persistedState = spaceTypes
    }
  })

  return spaceTypes
}
